import { fetchGroupPrices } from '@gn-audio/core/api/pricing/fetch-group-prices';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { IAttribute, IImage, IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { IEcomSetting } from '@gn-audio/core/api/site-settings/models/ecom-setting';
import { SegmentType } from '@gn-audio/core/common/enums';
import { fromJson } from 'angular';
import { getColorStyles, IColorStyles } from 'src/Foundation/Design/platform';
import { Filter, IFilter, IFilterServiceDTO, IOption } from '../models';
import { ICustomizerFilterService } from '../services/customizer-filter.service';
import { fetchFamilyOrGroupsAttributes, sortGroupsByPrice } from '../utils';

export interface IFamilyCustomizerScopeConfiguration {
  skuOnInit?: string;
  familyId?: string;
  groupIds?: string;
  filtersJSON?: string;
}

/**
 * @deprecated Should be converted into component!
 */
export interface IFamilyCustomizerScope extends ng.IScope {
  configuration: IFamilyCustomizerScopeConfiguration;
  price: IFromPrice | ISFCCGroupPrice;
  groups: IProductWithAttributes[];
  selection: IProductWithAttributes;
  selections: IProductWithAttributes[];
  filters: IFilter[];

  filterChange(filter?: IFilter): void;
  onPriceLoad(price: IFromPrice | ISFCCGroupPrice): void;
  toggle(filter: IFilter, optionValue: string): void;
  whereToBuy(): string;
  getFilterLabelStyle(stringifiedColors: string): IColorStyles;
  isColorFilter(filter: IFilter): boolean;
}

export class FamilyCustomizerController implements ng.IController {
  public static $inject: ReadonlyArray<string> = ['$scope', '$q', '$location', 'customizerFilterService'];

  protected filterTemplateSuffix = 'family-customizer-spot-filter-';
  protected filterOptionHistory: { [key: string]: string; } = {};

  private initiailized = false;

  constructor(
    protected $scope: IFamilyCustomizerScope,
    protected $q: ng.IQService,
    protected $location: ng.ILocationService,
    protected filterService: ICustomizerFilterService,
  ) {
    this.$scope.configuration = {
      skuOnInit: this.getSkuFromUrl(),
    };

    if (this.$location.path() === '') {
      this.$location.path('/').replace();
    }

    this.$scope.onPriceLoad = (price: IFromPrice | ISFCCGroupPrice) => {
      this.$scope.price = price;
    };

    this.$scope.isColorFilter = (filter: IFilter): boolean => {
      return (
        filter
          .template()
          .toLowerCase()
          .indexOf('color') > -1
      );
    };

    this.$scope.toggle = (filter: IFilter, optionValue: string) => {
      const optionHistoryValue: string | undefined = this.filterOptionHistory[filter.key];

      if (optionHistoryValue === optionValue) {
        delete this.filterOptionHistory[filter.key];
        filter.value = undefined;
        this.$scope.filterChange(filter);
      } else {
        this.filterOptionHistory[filter.key] = optionValue;
      }
    };

    this.$scope.filterChange = () => {
      this.filter();
    };

    this.$scope.getFilterLabelStyle = (stringifiedColors: string): IColorStyles => {
      return getColorStyles(stringifiedColors);
    };

    this.$scope.whereToBuy = (): string => {
      if (!this.$scope.selection) {
        return '';
      }

      if (this.$scope.selection.segmentType === SegmentType.Business) {
        return '/where-to-buy/partner-locator';
      }

      return '/where-to-buy/retailer-locator';
    };

    const filterJsonWatcher = this.$scope.$watch('configuration.filtersJSON', () => {
      filterJsonWatcher();
      if (this.$scope.configuration.filtersJSON === undefined) {
        return;
      }

      this.$scope.filters = fromJson(this.$scope.configuration.filtersJSON)
        .map((fDto: IFilterServiceDTO) =>
          Filter.initFromService(fDto, this.filterTemplateSuffix),
        );
      this.$scope.filters.forEach((f: IFilter) => {
        f.options.forEach((o: IOption) => {
          o.disabled = false;
          o.visible = false;
        });
      });
    });

    const $productsConfigurationWatcher = this.$scope.$watch('configuration.familyId + cofiguration.groupIds', () => {
      $productsConfigurationWatcher();
      const familyId: number = Number(this.$scope.configuration.familyId);
      const groupIds: number[] = fromJson(this.$scope.configuration.groupIds);
      this.$q.when(fetchFamilyOrGroupsAttributes(familyId, groupIds))
        .then(this.onGetFamilyByIdSuccess.bind(this));
    });
  }

  public preSelectColor() {
    let colorFilters: IFilter[];
    let colorAttributes: IAttribute[];
    const colorFilterKey = 'Product_Mobcolorhex';

    if (!this.$scope.filters || this.$scope.filters.length === 0) {
      return;
    }

    if (!this.$scope.selection) {
      return;
    }

    colorAttributes = this.$scope.selection.attributes.filter((a: IAttribute) => a.key === colorFilterKey);
    if (colorAttributes.length === 0) {
      return;
    }

    colorFilters = this.$scope.filters.filter((f: IFilter) => f.key === colorFilterKey);
    if (colorFilters.length === 0) {
      return;
    }

    colorFilters[0].value = colorAttributes[0].value;
    this.$scope.toggle(colorFilters[0], colorFilters[0].value);
  }

  public filter() {
    if (!this.initiailized) {
      return;
    }

    this.$scope.selections = this.filterService.filter(this.$scope.groups, this.$scope.filters);
    if (this.anyFilterSelected()) {
      this.$scope.selection = this.$scope.selections[0];
    } else {
      this.setInitialSelection(this.$scope.selections);
    }

    this.setSkuInUrl(this.$scope.selection.sku);
    this.filterService.process(this.$scope.groups, this.$scope.filters);
  }

  /** 
   * @async
   */
  protected onGetFamilyByIdSuccess(groups: IProductWithAttributes[]) {
    if (groups.length === 0) {
      console.warn(`No products were loaded for ${this.$scope.configuration.familyId}`);
      return;
    }

    const groupIds: number[] = groups.map((g: IProductWithAttributes) => g.groupId);
    this.$q.when(fetchEcomSetting())
      .then((ecomSetting: IEcomSetting | null) => {
        if (!ecomSetting?.ecomEnabled) {
          this.onFamilyGroupsPrepared(groups);
        } else {
          this.$q.when(fetchGroupPrices(groupIds))
            .then((prices: IFromPrice[] | ISFCCGroupPrice[]) => {
              const sortedGroups = sortGroupsByPrice(groups, prices);
              this.onFamilyGroupsPrepared(sortedGroups);
            });
        }
      });
  }

  protected onFamilyGroupsPrepared(groups: IProductWithAttributes[]) {
    this.$scope.groups = groups;
    this.$scope.groups.forEach((g: IProductWithAttributes) => {
      g.images = g.images.filter((image: IImage): boolean => image.url.match(/.(jpg|jpeg|png|tiff|tif)/gi) !== null);
    });

    this.setInitialSelection(this.$scope.groups);
    this.filterService.prepare(groups, this.$scope.filters);

    /** Only for Consumer products */
    this.preSelectColor();

    this.initiailized = true;
    this.filter();
  }

  private anyFilterSelected(): boolean {
    return (this.$scope.filters || []).filter((f: IFilter): boolean => f.hasValue()).length > 0;
  }

  private setInitialSelection(groups: IProductWithAttributes[]) {
    const intialSku: string = this.$scope.configuration.skuOnInit;
    let selection: IProductWithAttributes;

    if (intialSku) {
      selection = groups.filter((g: IProductWithAttributes): boolean => g.sku === intialSku)[0];
    }

    if (!selection) {
      selection = groups[0];
    }

    this.$scope.selection = selection;
  }

  private getSkuFromUrl(): string {
    return this.$location.hash() || '';
  }

  private setSkuInUrl(sku: string) {
    this.$location.hash(sku);
  }
}
